import React from 'react';
import { Box, Typography, LinearProgress, IconButton, Paper, Chip } from '@mui/material';
import { CheckCircle, Error as ErrorIcon, Close, HourglassEmpty, CloudUpload } from '@mui/icons-material';
import { useUploadContext } from '../../context/UploadContext';

const STAGE_LABELS = {
  queued: 'Queued',
  s3_upload: 'Uploading to cloud',
  text_extraction: 'Extracting text',
  shadow_file: 'Creating index',
  kb_ingestion: 'KB ingestion',
  properties: 'Extracting properties',
  indexing: 'Indexing',
  done: 'Done',
};

export default function GlobalUploadPanel() {
  const { jobs, showPanel, setShowPanel, hasActive, completedCount, totalCount, clearCompleted } = useUploadContext();

  const allEntries = Object.entries(jobs).map(([id, job]) => ({ id, ...job }));
  if (!showPanel || allEntries.length === 0) return null;

  const handleClose = () => {
    clearCompleted();
    if (!hasActive) setShowPanel(false);
  };

  const displayCount = completedCount;
  const displayTotal = totalCount;

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        width: 360,
        maxHeight: 360,
        zIndex: 1300,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2, py: 1, bgcolor: '#667eea', color: 'white',
      }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Uploads ({displayCount}/{displayTotal})
        </Typography>
        <IconButton size="small" onClick={handleClose} sx={{ color: 'white' }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
        {allEntries.map(entry => (
          <Box key={entry.id} sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              {entry.status === 'completed' && <CheckCircle sx={{ fontSize: 16, color: '#2e7d32' }} />}
              {entry.status === 'error' && <ErrorIcon sx={{ fontSize: 16, color: '#c62828' }} />}
              {entry.status === 'uploading' && <CloudUpload sx={{ fontSize: 16, color: '#667eea' }} />}
              {entry.status === 'processing' && <HourglassEmpty sx={{ fontSize: 16, color: '#e65100' }} />}
              {entry.status === 'queued' && <HourglassEmpty sx={{ fontSize: 16, color: '#757575' }} />}
              <Typography variant="body2" noWrap sx={{ flex: 1, fontSize: '0.8rem' }}>
                {entry.fileName || entry.name}
              </Typography>
              {(entry.status === 'uploading' || entry.status === 'processing') && (
                <Chip label={`${entry.progress || 0}%`} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
              )}
            </Box>
            {(entry.status === 'uploading' || entry.status === 'processing' || entry.status === 'queued') && (
              <>
                <LinearProgress
                  variant={entry.status === 'queued' ? 'indeterminate' : 'determinate'}
                  value={entry.progress || 0}
                  sx={{
                    borderRadius: 1, height: 4,
                    '& .MuiLinearProgress-bar': {
                      bgcolor: entry.status === 'processing' ? '#e65100' : '#667eea',
                    },
                  }}
                />
                {entry.stage && entry.status === 'processing' && (
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#e65100', mt: 0.25, display: 'block' }}>
                    {STAGE_LABELS[entry.stage] || entry.stage}
                  </Typography>
                )}
              </>
            )}
            {entry.status === 'error' && (
              <Typography variant="caption" color="error" sx={{ fontSize: '0.7rem' }}>
                {typeof entry.error === 'string' ? entry.error : 'Upload failed'}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Paper>
  );
}
